import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, Card, Empty, Input, Modal, Select, Space, Spin, Table, Tabs, Tag, Typography, message } from 'antd'
import { CheckCircleOutlined, RobotOutlined, SafetyCertificateOutlined } from '@ant-design/icons'
import { useLocation } from 'react-router-dom'
import {
  confirmAicReliabilityRule,
  createAicReliabilityRule,
  generateAicReliabilityRule,
  getAicImportSchedules,
  getAicDashboardDatasetDetail,
  getAicReliabilityRules
} from '../../request'
import {
  buildDatasetTableOptions,
  candidateUnavailableMessage,
  display,
  formatDateTime,
  formatSchedulePeriod,
  isCandidateSavable,
  isScheduleCandidateSavable,
  ruleStatusMeta,
  scheduleStatusMeta,
  unwrapList
} from './aicDashboardUtils'

const RULE_TYPES = ['delay', 'coverage', 'quality']

const RULE_LABELS = {
  delay: '导入计划',
  coverage: '数据截止日期',
  quality: '字段质量规则'
}

const initialDrafts = {
  delay: {
    periodType: 'weekly',
    description: '',
    timezone: 'Asia/Shanghai',
    calendar: 'natural',
    gracePeriodHours: 0
  },
  coverage: { tableId: undefined, fieldName: undefined, granularity: 'day', description: '' },
  quality: { tableId: undefined, fieldName: undefined, severity: 'warning', description: '' }
}

const cloneDrafts = () => ({
  delay: { ...initialDrafts.delay },
  coverage: { ...initialDrafts.coverage },
  quality: { ...initialDrafts.quality }
})

const errorMessage = (error, fallback) => (
  (error && error.response && error.response.data && (error.response.data.msg || error.response.data.message)) ||
  (error && (error.msg || error.message)) || fallback
)

function RuleStatus ({ value }) {
  const meta = ruleStatusMeta(value)
  return <Tag color={meta.color}>{meta.label}</Tag>
}

const schedulePreviewColumns = [
  { title: '序号', dataIndex: 'sequenceNo', width: 72 },
  { title: '周期', dataIndex: 'periodKey', width: 130, render: value => display(value) },
  { title: '周期开始', dataIndex: 'windowStartAt', width: 180, render: value => formatDateTime(value, '-') },
  { title: '导入截止时间', dataIndex: 'deadlineAt', width: 180, render: value => formatDateTime(value, '-') },
  { title: '宽限截止时间', dataIndex: 'graceDeadlineAt', width: 180, render: value => formatDateTime(value, '-') }
]

const persistedScheduleColumns = [
  ...schedulePreviewColumns,
  {
    title: '状态',
    dataIndex: 'status',
    width: 100,
    render: value => {
      const meta = scheduleStatusMeta(value)
      return <Tag color={meta.color}>{meta.label}</Tag>
    }
  },
  { title: '完成上传时间', dataIndex: 'satisfiedUploadAt', width: 180, render: value => formatDateTime(value, '-') }
]

function CandidatePreview ({ candidate, type }) {
  if (!candidate) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={type === 'delay' ? '填写导入规则后，让 AI 生成未来 50 期计划' : '填写规则说明后生成 AI 候选方案'} />
  }
  const risks = Array.isArray(candidate.risks) ? candidate.risks : []
  const ambiguities = Array.isArray(candidate.ambiguities) ? candidate.ambiguities : []
  const isSchedule = type === 'delay'
  const savable = isSchedule ? isScheduleCandidateSavable(candidate) : isCandidateSavable(candidate)
  const preview = candidate.schedulePreview || {}
  const scheduleItems = Array.isArray(preview.items) ? preview.items : []
  return <div className="aic-candidate-preview">
    <Alert
      showIcon
      type={savable ? 'success' : 'warning'}
      message={savable ? (isSchedule ? `已生成 ${scheduleItems.length} 期导入计划，请核对截止时间` : '服务端静态安全检查通过，等待人工确认') : '候选规则暂不可用'}
      description={display(candidate.explanation, '服务端未返回说明')}
    />
    {(ambiguities.length > 0 || risks.length > 0) && <div className="aic-candidate-notes">
      {ambiguities.length > 0 && <div><strong>需要澄清</strong><ul>{ambiguities.map((item, index) => <li key={`ambiguity-${index}`}>{item}</li>)}</ul></div>}
      {risks.length > 0 && <div><strong>风险提示</strong><ul>{risks.map((item, index) => <li key={`risk-${index}`}>{item}</li>)}</ul></div>}
    </div>}
    {isSchedule
      ? <>
        <div className="aic-schedule-summary">
          <Tag color="blue">{formatSchedulePeriod(preview.periodType)}</Tag>
          <span>时区：{display(preview.timezone, 'Asia/Shanghai')}</span>
          <span>宽限：{Number(preview.gracePeriodHours) || 0} 小时</span>
          <span>共 {scheduleItems.length} 期</span>
        </div>
        <Table
          className="aic-schedule-table"
          size="small"
          rowKey={(item, index) => item.periodKey || item.sequenceNo || index}
          columns={schedulePreviewColumns}
          dataSource={scheduleItems}
          pagination={{ pageSize: 10, showSizeChanger: false, showTotal: total => `共 ${total} 期` }}
          scroll={{ x: 760 }}
        />
      </>
      : <div className="aic-code-block">
        <div className="aic-code-block-title">候选 SQL</div>
        <pre>{candidate.generatedSql || '未生成 SQL，不能保存或启用该规则'}</pre>
      </div>}
    {candidate.normalizedRuleJson && <div className="aic-code-block">
      <div className="aic-code-block-title">{isSchedule ? 'AI 解析结果（用于固化计划）' : '结构化规则'}</div>
      <pre>{candidate.normalizedRuleJson}</pre>
    </div>}
  </div>
}

export default function AicReliabilitySettings ({ datasets, Nav }) {
  const location = useLocation()
  const queryDatasetId = useMemo(() => new URLSearchParams(location.search).get('datasetId'), [location.search])
  const [datasetId, setDatasetId] = useState(undefined)
  const [detail, setDetail] = useState(null)
  const [activeType, setActiveType] = useState('delay')
  const [rules, setRules] = useState({ delay: [], coverage: [], quality: [] })
  const [drafts, setDrafts] = useState(cloneDrafts)
  const [candidate, setCandidate] = useState(null)
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [scheduleVisible, setScheduleVisible] = useState(false)
  const [scheduleLoading, setScheduleLoading] = useState(false)
  const [scheduleRule, setScheduleRule] = useState(null)
  const [scheduleItems, setScheduleItems] = useState([])
  const [error, setError] = useState('')

  const selectedDataset = useMemo(
    () => datasets.find(item => String(item.datasetId) === String(datasetId)),
    [datasets, datasetId]
  )
  const fields = detail && Array.isArray(detail.fields) ? detail.fields : []
  const tables = useMemo(() => buildDatasetTableOptions(detail), [detail])
  const activeDraft = drafts[activeType]
  const activeFields = activeType === 'delay' || !activeDraft.tableId
    ? []
    : fields.filter(field => String(field.tableId) === String(activeDraft.tableId))

  const loadRules = useCallback(async (selectedId, projectId) => {
    setLoading(true)
    setError('')
    setCandidate(null)
    try {
      const [datasetDetail, delay, coverage, quality] = await Promise.all([
        getAicDashboardDatasetDetail(projectId),
        getAicReliabilityRules('delay', selectedId),
        getAicReliabilityRules('coverage', selectedId),
        getAicReliabilityRules('quality', selectedId)
      ])
      setDetail(datasetDetail || null)
      setRules({ delay: unwrapList(delay), coverage: unwrapList(coverage), quality: unwrapList(quality) })
    } catch (requestError) {
      setDetail(null)
      setRules({ delay: [], coverage: [], quality: [] })
      setError(errorMessage(requestError, '规则配置加载失败，请稍后重试'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (datasetId || datasets.length === 0) return
    const requested = datasets.find(item => String(item.datasetId) === String(queryDatasetId))
    setDatasetId((requested || datasets[0]).datasetId)
  }, [datasets, datasetId, queryDatasetId])

  useEffect(() => {
    if (!datasetId) return
    const dataset = datasets.find(item => String(item.datasetId) === String(datasetId))
    if (dataset && dataset.projectId) loadRules(datasetId, dataset.projectId)
  }, [datasetId, datasets, loadRules])

  useEffect(() => {
    if (activeType === 'delay' || tables.length === 0 || activeDraft.tableId) return
    setDrafts(current => ({
      ...current,
      [activeType]: { ...current[activeType], tableId: tables[0].value, fieldName: undefined }
    }))
  }, [activeType, activeDraft.tableId, tables])

  const updateDraft = patch => {
    setCandidate(null)
    setDrafts(current => ({ ...current, [activeType]: { ...current[activeType], ...patch } }))
  }

  const generateCandidate = async () => {
    const draft = drafts[activeType]
    if (activeType === 'delay' && !draft.description.trim()) {
      message.warning('请填写每期导入截止时间的规则说明')
      return
    }
    if (activeType !== 'delay' && (!draft.tableId || !draft.fieldName || !draft.description.trim())) {
      message.warning('请选择逻辑表和字段，并填写规则说明')
      return
    }
    setGenerating(true)
    setCandidate(null)
    try {
      const body = activeType === 'delay'
        ? {
            periodType: draft.periodType,
            description: draft.description.trim(),
            timezone: draft.timezone,
            gracePeriodHours: Number(draft.gracePeriodHours) || 0,
            count: 50
          }
        : activeType === 'coverage'
          ? { tableId: draft.tableId, dateField: draft.fieldName, description: draft.description.trim() }
          : { tableId: draft.tableId, fieldName: draft.fieldName, description: draft.description.trim() }
      const result = await generateAicReliabilityRule(datasetId, activeType, body)
      setCandidate(result || null)
      if (activeType === 'delay') {
        if (!isScheduleCandidateSavable(result)) {
          message.warning(candidateUnavailableMessage(result, 'AI 未生成可保存的导入计划，请查看候选结果中的提示'))
        }
      } else if (!isCandidateSavable(result)) {
        message.warning(candidateUnavailableMessage(result, '服务端未返回通过静态安全检查的候选 SQL'))
      }
    } catch (requestError) {
      message.error(errorMessage(requestError, 'AI 候选生成失败'))
    } finally {
      setGenerating(false)
    }
  }

  const saveDraft = async () => {
    const draft = drafts[activeType]
    if (!selectedDataset || !selectedDataset.projectId) {
      message.error('数据源信息不完整，请刷新后重试')
      return
    }
    let body
    if (activeType === 'delay') {
      if (!isScheduleCandidateSavable(candidate)) {
        message.warning('请先生成并核对可用的未来导入计划')
        return
      }
      body = {
        datasetId,
        naturalLanguageRule: draft.description.trim(),
        normalizedRuleJson: candidate.normalizedRuleJson,
        timezone: draft.timezone,
        calendar: draft.calendar,
        gracePeriodHours: Number(draft.gracePeriodHours) || 0
      }
    } else {
      if (!isCandidateSavable(candidate)) {
        message.warning('请先生成通过服务端安全检查的候选 SQL')
        return
      }
      body = activeType === 'coverage'
        ? {
            datasetId,
            tableId: draft.tableId,
            dateField: draft.fieldName,
            granularity: draft.granularity,
            filterJson: '{}',
            naturalLanguageRule: draft.description.trim(),
            generatedSql: candidate.generatedSql,
            normalizedSql: candidate.generatedSql,
            validationStatus: 'passed',
            validationMessage: candidate.explanation
          }
        : {
            datasetId,
            tableId: draft.tableId,
            fieldName: draft.fieldName,
            description: draft.description.trim(),
            ruleDslJson: candidate.normalizedRuleJson || '{}',
            generatedSql: candidate.generatedSql,
            normalizedSql: candidate.generatedSql,
            severity: draft.severity,
            validationStatus: 'passed',
            validationMessage: candidate.explanation
          }
    }
    setSaving(true)
    try {
      await createAicReliabilityRule(activeType, body)
      message.success('规则草稿已保存，尚未启用')
      setCandidate(null)
      await loadRules(datasetId, selectedDataset.projectId)
    } catch (requestError) {
      message.error(errorMessage(requestError, '规则草稿保存失败'))
    } finally {
      setSaving(false)
    }
  }

  const showSchedules = async rule => {
    setScheduleRule(rule)
    setScheduleItems([])
    setScheduleVisible(true)
    setScheduleLoading(true)
    try {
      const result = await getAicImportSchedules(rule.id)
      setScheduleItems(unwrapList(result))
    } catch (requestError) {
      message.error(errorMessage(requestError, '导入计划加载失败'))
    } finally {
      setScheduleLoading(false)
    }
  }

  const confirmRule = rule => {
    if (!selectedDataset || !selectedDataset.projectId) {
      message.error('数据源信息不完整，请刷新后重试')
      return
    }
    Modal.confirm({
      title: `确认启用${RULE_LABELS[activeType]} v${rule.version}？`,
      content: activeType === 'delay'
        ? '确认后该版本不可再编辑，并会固化 AI 生成的未来 50 期截止时间，后续按这些时间判断是否按时上传。'
        : '确认后该版本不可再编辑。此操作只启用规则，不会立即执行规则 SQL。',
      okText: '确认启用',
      cancelText: '取消',
      onOk: async () => {
        try {
          const confirmedRule = await confirmAicReliabilityRule(activeType, rule.id)
          message.success(activeType === 'delay' ? '导入计划已确认并固化' : '规则已启用')
          await loadRules(datasetId, selectedDataset.projectId)
          if (activeType === 'delay') await showSchedules(confirmedRule && confirmedRule.id ? confirmedRule : rule)
        } catch (requestError) {
          message.error(errorMessage(requestError, '规则确认失败'))
          throw requestError
        }
      }
    })
  }

  const ruleColumns = [
    { title: '版本', dataIndex: 'version', width: 82, render: value => `v${display(value)}` },
    { title: '状态', dataIndex: 'status', width: 100, render: value => <RuleStatus value={value} /> },
    {
      title: '规则内容',
      render: (_, rule) => display(rule.naturalLanguageRule || rule.description)
    },
    { title: '安全校验', dataIndex: 'validationStatus', width: 110, render: value => value ? <Badge status={value === 'passed' ? 'success' : 'warning'} text={value === 'passed' ? '已通过' : display(value)} /> : '-' },
    { title: '更新时间', dataIndex: 'updatedAt', width: 175, render: value => formatDateTime(value, '-') },
    {
      title: '操作',
      width: activeType === 'delay' ? 170 : 100,
      render: (_, rule) => <Space size={4}>
        {rule.status === 'draft' && <Button type="link" onClick={() => confirmRule(rule)}>确认启用</Button>}
        {activeType === 'delay' && rule.status !== 'draft' && <Button type="link" onClick={() => showSchedules(rule)}>查看计划</Button>}
        {rule.status !== 'draft' && activeType !== 'delay' && <span className="aic-muted">只读</span>}
      </Space>
    }
  ]

  const renderDelayEditor = () => <>
    <Alert
      className="aic-import-plan-guide"
      showIcon
      type="info"
      message="先由 AI 生成未来 50 期截止时间，再由你核对并确认"
      description="生成不会保存；保存草稿也不会生效。只有确认启用后，系统才会固化这 50 期计划并用于断更判断。"
    />
    <div className="aic-rule-form-grid">
      <div className="aic-form-field">
        <span id="aic-delay-period-label">导入周期</span>
        <Select
          aria-labelledby="aic-delay-period-label"
          value={drafts.delay.periodType}
          onChange={value => updateDraft({ periodType: value })}
          options={[
            { value: 'daily', label: '日' },
            { value: 'weekly', label: '周' },
            { value: 'monthly', label: '月' },
            { value: 'irregular', label: '不定期' }
          ]}
        />
      </div>
      <div className="aic-form-field">
        <span id="aic-delay-timezone-label">时区</span>
        <Select aria-labelledby="aic-delay-timezone-label" value={drafts.delay.timezone} onChange={value => updateDraft({ timezone: value })} options={[{ value: 'Asia/Shanghai', label: 'Asia/Shanghai' }]} />
      </div>
      <div className="aic-form-field">
        <label htmlFor="aic-delay-grace-hours">宽限小时数</label>
        <Input id="aic-delay-grace-hours" type="number" min={0} value={drafts.delay.gracePeriodHours} onChange={event => updateDraft({ gracePeriodHours: event.target.value })} />
      </div>
      <div className="aic-form-field aic-form-field-wide">
        <label htmlFor="aic-delay-description">导入时间规则说明</label>
        <Input.TextArea
          id="aic-delay-description"
          rows={5}
          value={drafts.delay.description}
          onChange={event => updateDraft({ description: event.target.value })}
          placeholder={drafts.delay.periodType === 'daily'
            ? '例如：每天 18:00 前完成当天数据上传'
            : drafts.delay.periodType === 'weekly'
              ? '例如：每周日 24:00 前完成本周数据上传'
              : drafts.delay.periodType === 'monthly'
                ? '例如：每月最后一天 18:00 前完成当月数据上传'
                : '例如：请明确列出未来各次计划上传的日期和时间'}
          maxLength={2000}
          showCount
        />
      </div>
    </div>
    <div className="aic-rule-actions">
      <Button type="primary" icon={<RobotOutlined />} loading={generating} onClick={generateCandidate}>生成未来 50 期</Button>
      <span className="aic-muted">宽限时间请使用上方独立字段配置；保存前请重点核对首期、跨月和节假日附近的截止时间。</span>
    </div>
    <CandidatePreview candidate={candidate} type="delay" />
  </>

  const renderAiEditor = type => {
    const draft = drafts[type]
    const isCoverage = type === 'coverage'
    return <>
      {fields.length === 0
        ? <Alert showIcon type="info" message="该数据源还没有字段元数据，暂时不能配置 AI 规则" />
        : <div className="aic-rule-form-grid">
          <div className="aic-form-field">
            <span id={`aic-${type}-table-label`}>逻辑表</span>
            <Select
              aria-labelledby={`aic-${type}-table-label`}
              value={draft.tableId}
              onChange={value => updateDraft({ tableId: value, fieldName: undefined })}
              options={tables}
              placeholder="请选择逻辑表"
            />
          </div>
          <div className="aic-form-field">
            <span id={`aic-${type}-field-label`}>{isCoverage ? '数据日期字段' : '校验字段'}</span>
            <Select
              aria-labelledby={`aic-${type}-field-label`}
              showSearch
              optionFilterProp="label"
              value={draft.fieldName}
              onChange={value => updateDraft({ fieldName: value })}
              options={activeFields.map(field => ({ value: field.fieldName, label: `${field.fieldName}${field.displayName ? ` · ${field.displayName}` : ''}` }))}
              placeholder="请选择字段"
            />
          </div>
          <div className="aic-form-field">
            <span id={`aic-${type}-option-label`}>{isCoverage ? '日期粒度' : '异常等级'}</span>
            {isCoverage
              ? <Select aria-labelledby={`aic-${type}-option-label`} value={draft.granularity} onChange={value => updateDraft({ granularity: value })} options={[{ value: 'day', label: '日' }, { value: 'week', label: '周' }, { value: 'month', label: '月' }, { value: 'quarter', label: '季度' }]} />
              : <Select aria-labelledby={`aic-${type}-option-label`} value={draft.severity} onChange={value => updateDraft({ severity: value })} options={[{ value: 'info', label: '提示' }, { value: 'warning', label: '预警' }, { value: 'critical', label: '严重' }]} />}
          </div>
          <div className="aic-form-field aic-form-field-wide">
            <label htmlFor={`aic-${type}-description`}>{isCoverage ? '最新数据截止日期规则说明' : '字段缺失或报错规则说明'}</label>
            <Input.TextArea
              id={`aic-${type}-description`}
              rows={5}
              value={draft.description}
              onChange={event => updateDraft({ description: event.target.value })}
              placeholder={isCoverage ? '例如：按自然日统计，查询订单日期字段中的最大日期' : '例如：订单号不能为空，空字符串也视为缺失'}
              maxLength={2000}
              showCount
            />
          </div>
        </div>}
      <div className="aic-rule-actions">
        <Button type="primary" icon={<RobotOutlined />} loading={generating} disabled={fields.length === 0} onClick={generateCandidate}>生成 AI 候选</Button>
        <span className="aic-muted">AI 只生成候选，服务端安全校验和人工确认后才能启用。</span>
      </div>
      <CandidatePreview candidate={candidate} type={type} />
    </>
  }

  return <div>
    <Nav active="settings" />
    <div className="aic-page-header aic-settings-header">
      <Typography.Title level={1}>可靠性规则</Typography.Title>
      <Typography.Paragraph type="secondary">配置导入计划、数据截止日期和字段质量判定。规则先由 AI 生成候选，人工确认后启用。</Typography.Paragraph>
    </div>
    <Card className="aic-panel aic-dataset-picker">
      <div>
        <div className="aic-section-eyebrow">配置对象</div>
        <Typography.Title level={3}>{selectedDataset ? selectedDataset.projectName : '选择数据源'}</Typography.Title>
        <div className="aic-muted">仅展示当前账号有权查看的数据源；规则确认仍由后端校验 Owner 权限。</div>
      </div>
      <Select
        aria-label="选择要配置可靠性规则的数据源"
        showSearch
        optionFilterProp="label"
        value={datasetId}
        onChange={setDatasetId}
        placeholder="请选择数据源"
        options={datasets.map(item => ({ value: item.datasetId, label: item.projectName || `数据源 #${item.datasetId}` }))}
      />
    </Card>
    {error && <Alert className="aic-settings-alert" showIcon type="error" message={error} />}
    {!datasetId
      ? <Card className="aic-panel"><Empty description="当前没有可配置的数据源" /></Card>
      : <Spin spinning={loading}>
        <div className="aic-settings-grid">
          <Card className="aic-panel aic-rule-editor" title={<span><SafetyCertificateOutlined /> 规则配置</span>}>
            <Tabs activeKey={activeType} onChange={value => { setActiveType(value); setCandidate(null) }}>
              <Tabs.TabPane tab="导入计划" key="delay" />
              <Tabs.TabPane tab="数据截止日期" key="coverage" />
              <Tabs.TabPane tab="字段质量" key="quality" />
            </Tabs>
            {activeType === 'delay' ? renderDelayEditor() : renderAiEditor(activeType)}
            <div className="aic-save-bar">
              <div><CheckCircleOutlined /> {activeType === 'delay' ? '保存后仍是草稿；确认启用时才会固化预览中的 50 期计划。' : '保存后仍是草稿，不会立即执行规则。'}</div>
              <Button loading={saving} onClick={saveDraft}>{activeType === 'delay' ? '保存计划草稿' : '保存草稿'}</Button>
            </div>
          </Card>
          <Card className="aic-panel aic-rule-history" title={`${RULE_LABELS[activeType]}版本`}>
            <Table
              size="small"
              rowKey={rule => rule.id || `${activeType}-${rule.version}`}
              columns={ruleColumns}
              dataSource={rules[activeType] || []}
              pagination={false}
              scroll={{ x: 760 }}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无规则版本" /> }}
            />
          </Card>
        </div>
      </Spin>}
    <Modal
      title={`${scheduleRule ? `导入计划 v${display(scheduleRule.version)}` : '导入计划'} · 已固化截止时间`}
      visible={scheduleVisible}
      width={960}
      footer={<Button type="primary" onClick={() => setScheduleVisible(false)}>关闭</Button>}
      onCancel={() => setScheduleVisible(false)}
      destroyOnClose
    >
      <Alert className="aic-schedule-modal-note" showIcon type="info" message="以下时间已保存，系统将按每一期的宽限截止时间判断是否断更。" />
      <Table
        size="small"
        loading={scheduleLoading}
        rowKey={(item, index) => item.id || item.periodKey || item.sequenceNo || index}
        columns={persistedScheduleColumns}
        dataSource={scheduleItems}
        pagination={{ pageSize: 10, showSizeChanger: false, showTotal: total => `共 ${total} 期` }}
        scroll={{ x: 960 }}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="当前版本尚未生成固化计划" /> }}
      />
    </Modal>
  </div>
}
